import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useNavigate from "hooks/useNavigate";
import {
	accessToShowMarketplace,
	setSelectedBussProduct,
} from "pages/BussProductsPage/ducks/BussProductSlice";
import {
	getSessionCart,
	setEditPackagePackageId,
	setIsEditPackageFlow,
	setSelectedGWProduct,
	setNavigatePath,
	setEntryPoint,
	setCompletedPagesForHPB,
	setStorePreviousPaths,
} from "app/ducks/GlobalSlice";
import * as SiteAnalytics from "sitecatalyst";
import { getPathName } from "common/components/HorizontalProgress/VZStepperGetSteps";

const AssignNumber =  React.lazy(() => import("pages/AssignNumber/AssignNumber"));
const AccountsModal = React.lazy(() => import("pages/CheckoutNew/Components/AccountsModal"));
const MultiFWA =  React.lazy(() => import("pages/MultiFWA/MultiFWA"));
const ByodMonarch = React.lazy(() => import("pages/Mobility/components/MobilityByod/Monarch/ByodMonarch"));
const SelectAccount = React.lazy(() => import ("pages/SelectAccount/SelectAccount"));
const NSEGridwall = React.lazy(() =>
	import("pages/Mobility/components/MobilityGridwall/MobilityGridwall")
);
const AccessoryGridwall = React.lazy(() =>
	import("pages/Accessories/components/AccessoryGridwall/AccessoryGridwall")
);
const AccessoryProductDetails = React.lazy(()=>
	import("pages/Accessories/components/AccessoryProductDetails/AccessoryProductDetails")
);
const MobilityProductDetails = React.lazy(() =>
	import("pages/Mobility/components/MobilityProductDetails")
);
const MobilityBYOD = React.lazy(() =>
	import("pages/Byod/Byod")
);
const TradeInNew = React.lazy(() =>
	import("pages/TradeIn/TradeInNew/TradeInNew")
);
const ByodBulkUpload = React.lazy(() => import("pages/Byod/ByodBulkUpload"));
const PlansLanding = React.lazy(() =>
	import("pages/Mobility/components/PlansNew/PlansLanding/PlansLanding")
);
const FeaturesLanding = React.lazy(() =>
	import(
		"pages/Mobility/components/FeaturesNew/Components/FeaturesLanding/FeaturesLanding"
	)
);
const ShoppingCart = React.lazy(() =>
	import("pages/ShoppingCart/ShoppingCart")
);
const Checkout = React.lazy(() => import("pages/Checkout"));
const CheckoutNew = React.lazy(() => import("pages/CheckoutNew/CheckoutNew"));
const ConfirmationVDS3 = React.lazy(() =>
	import("pages/Checkout/Confirmation/ConfirmationVDS3")
);
const MarketplaceProductDetails = React.lazy(() =>
	import(
		"pages/Marketplace/components/MobilityProductDetails/MarketplaceProductDetails"
	)
);
const MarketplaceGridwall = React.lazy(() =>
	import("pages/Marketplace/components/MobilityGridwall/MobilityGridwall")
);
const BussProductsAddOnPage = React.lazy(() =>
	import("pages/BussProductsPage/BussProductsAddOnPage")
);
const BussProductPDP = React.lazy(() =>
	import("pages/BussProductsPage/BussProductPDP")
);
const QualificationLanding = React.lazy(() =>
	import("pages/Qualification/QualificationLanding")
);
const QualificationMoverLanding = React.lazy(() =>
	import("pages/Qualification/QualificationMoverLanding")
);
const QualificationSelection = React.lazy(() =>
	import("pages/Qualification/QualificationSelection")
);
const ValidateRouters = React.lazy(() =>
	import("pages/Qualification/ValidateRouters")
);
const FWAAddons = React.lazy(() => import("pages/LandingAddons/FwaAddons"));
const ManageFeatures = React.lazy(() => import("pages/LandingAddons/ManageFeatures"));

const BackupAddons = React.lazy(() =>
	import("pages/LandingAddons/LandingAddonsNew")
);
const BIBulkUpload = React.lazy(() => import("pages/BulkUpload/BIBulkUpload"));
const BIBulkOrder = React.lazy(() => import("pages/BulkUpload/BIBulkOrder"));
const CompleteOrder = React.lazy(() =>
	import("pages/BulkUpload/CompleteOrder")
);
const MarketPlaceFios = React.lazy(() => import("./MarketPlaceFios"));
const LandingPage5G = React.lazy(() =>
	import("pages/Landing5G-V2/LandingPage5G")
);
const LandingPage4G = React.lazy(() =>
	import("pages/Landing4G-V2/LandingPage4G")
);
const Wcmp = React.lazy(() => import("pages/WCMP/Wcmp"));
const GridwallCompareDevices = React.lazy(() =>
	import("pages/Mobility/components/MobilityGridwall/GridwallCompareDevices")
);

export const WidgetRouter = ({
	entryPoint,
	ecpd,
	clientId,
	clientName,
	sessionId,
	flow,
	fromProspect,
	inputAttributes,
	...props
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [oldCheckout, setCheckoutSwitch] = React.useState(false);
	const globalData = useSelector((state) => state.ProspectWidgets_GlobalData);
	const navigatePath = useSelector(
		(state) => state.ProspectWidgets_GlobalData?.navigatePath
	);
	const sessionCartData = useSelector(
		(state) =>
			state.ProspectWidgets_GlobalData?.sessionCartData?.apiResponse
				?.prospectFlowData?.cart?.packages
	);
	const widgetInputParams = globalData?.widgetInputParams;
	const navStack = useRef([]);

	const ComponentPathMap = {
		checkout: oldCheckout ? (
			<CheckoutNew
				fromBUSS={entryPoint === "softwares"}
				fromIOTSIM={entryPoint === "WCMP"}
			/>
		) : (
			<Checkout />
		),
		"/shop/confirmation": <ConfirmationVDS3 fromIOTSIM={entryPoint === "WCMP"}  />,		
		"/shop/shopping-cart": (
			<ShoppingCart
				fromBUSS={entryPoint === "softwares"}
				fromAddALine={entryPoint === "newline"}
			/>
		),
		"gridwall/all": (
			<NSEGridwall
				layout="search"
				category="all"
				fromAddALine={true}
			/>
		),
		"gridwall/smartphones": (
			<NSEGridwall
				category="smartphones"
				isCategory={true}
				fromAddALine={true}
			/>
		),
		"gridwall/mobile-hotspots": (
			<NSEGridwall
				category="mobile-hotspots"
				isCategory={true}
				fromAddALine={true}
			/>
		),		
		"gridwall/deskphones": (
			<NSEGridwall
				category="deskphones"
				isCategory={true}
				fromAddALine={true}
			/>
		),
		"gridwall/tablets": (
			<NSEGridwall category="tablets" isCategory={true} fromAddALine={true} />
		),
		"gridwall/smart-watches": (
			<NSEGridwall category="smart-watches" isCategory={true} fromAddALine={true} />
		),
		"gridwall/laptops": (
			<NSEGridwall category="laptops" isCategory={true} fromAddALine={true} />
		),
		"gridwall/accessories": (
			<AccessoryGridwall category="all" isCategory={true} />
		),
		"gridwall/buss": (
			<MarketplaceGridwall
				fromBUSS={true}
				fromProspect={fromProspect}
				ecpd={ecpd}
			/>
		),
		"/shop/compare": <GridwallCompareDevices />,
		// "/shop/byod": <MobilityBYOD />,
		"/shop/accessories":<AccessoryGridwall />,
		"/shop/accessoryproductdetails":<AccessoryProductDetails />,
		"/shop/byodBulkUpload": <ByodBulkUpload />,
		"/shop/pdp": <MobilityProductDetails fromProspect={fromProspect} />,
		"/shop/accountselection":<AccountsModal isAccountsPage={true}/>,
		"/shop/trade-in-new":<TradeInNew />,
		"/shop/plans": <PlansLanding fromAddALine={entryPoint === "newline"} />,
		"/shop/features": <FeaturesLanding fromAddALine={entryPoint === "newline"} />,
		"/shop/select-account": <SelectAccount fromAddALine={entryPoint === "newline"} fromByod={entryPoint === "byod"} />,
		"/shop/assignNumbers": <AssignNumber fromAddALine={entryPoint === "newline"} fromByod={entryPoint === "byod"} /> ,
		"checkout/new": (
			<CheckoutNew
				fromBUSS={entryPoint === "softwares"}
				fromIOTSIM={entryPoint === "WCMP"}
				fromAddALine={entryPoint === "newline"}
			/>
		),
		"checkout/old": <Checkout />,
		"buss/addons": <BussProductsAddOnPage />,
		"buss/pdp": <BussProductPDP clientId={props.clientId} />,
		"marketplace/pdp": <MarketplaceProductDetails fromProspect={fromProspect} isAddonAlreadyExist={props?.isAddonAlreadyExist} />,
		"marketplace/gridwall": (
			<MarketplaceGridwall
				fromBUSS={true}
				fromProspect={fromProspect}
				ecpd={ecpd}
			/>
		),
		"fwa/qualification-landing": <QualificationLanding />,
		"fwa/qualification-selection": <QualificationSelection />,
		"fwa/validate-routers": <ValidateRouters />,
		"fwa/addons": <FWAAddons />,
		"fwa/manage-features": <ManageFeatures />,
		"fwa/backup": <BackupAddons />,
		"fwa/bulk-order": <BIBulkOrder />,
		"fwa/bulk-upload": <BIBulkUpload />,
		"fwa/complete-order": <CompleteOrder />,
		//"fwa/plans": globalData?.loadMultiFWAFlow ? <MultiFWA /> : globalData?.currentFlow === "5g" ? <LandingPage5G /> : <LandingPage4G />,
		"fwa/plans": <MultiFWA />,

		"fwa/5g/plans": <MultiFWA />,
		"fwa/4g/plans": <MultiFWA />,
		"fwa/5g/mmw": <LandingPage5G />,
		"fios-widget": <MarketPlaceFios />,
		"movers/movers-landing":<QualificationMoverLanding />,
		 "/shop/byod":<ByodMonarch />,
		wcmp: <Wcmp />,
	};
	const mappedComponent = ComponentPathMap?.[navigatePath];	
	const entryPointMatched = entryPoint === globalData?.entryPoint;
	useEffect(()=>{
		dispatch(setCompletedPagesForHPB(getPathName({storedPath:globalData?.completedPagedForHPB,navigatePath})))
	},[window.location.search,navigatePath])
	useEffect(() => {
		const currentUrl = window.location.href;
		const newUrl = new URL(currentUrl);
		const searchParams = new URLSearchParams(newUrl?.search);
		if (searchParams.has("pageName")) {
		const urlPath = decodeURIComponent(searchParams.get("pageName"));
		if (urlPath !== navigatePath) {
		dispatch(setNavigatePath(urlPath));
		}
		}
		const handleBackClick = () => {
			navStack.current.pop();
			const backPath = navStack.current?.[navStack.current?.length - 1];
		const currentUrl = window.location.href;
		const newUrl = new URL(currentUrl);
		const searchParams = new URLSearchParams(newUrl?.search);
		if (backPath && searchParams.has("pageName")) {
		newUrl.searchParams.set("pageName", encodeURIComponent(backPath));
				window.history.replaceState({}, "", newUrl);
		navigate(backPath);
		}
		};
		// Add the event listener
		window.addEventListener("popstate", handleBackClick);
		// Cleanup function to remove the listener on unmount
		return () => {
		window.removeEventListener("popstate", handleBackClick);
		};
	}, [navigate]);

	useEffect(() => {
		// const payload = {
		// 	"ecpdId": widgetInputParams?.ecpdId?.value ?? "",
		// 	"loginUserId": widgetInputParams?.mbLoginId?.value ?? widgetInputParams?.loginUserId?.value ?? widgetInputParams?.mbaUserId?.value ?? "",
		// 	"rootEcpdId": widgetInputParams?.ecpdId?.value ?? "",
		// 	"channel": widgetInputParams?.clientName?.value ?? ""
		// }
		// dispatch(accessToShowMarketplace(payload))
		//SiteAnalytics.trackPageInfo("gridwall");
		if (String(flow).toLowerCase() === "assisted") {
			setCheckoutSwitch(true);
		}

		if (!navigatePath) {
		const currentUrl = window.location.href;
		const newUrl = new URL(currentUrl);
			dispatch(setNavigatePath(defaultPath));
			newUrl.searchParams.set("pageName", encodeURIComponent(defaultPath));
			window && window.history.replaceState({}, "", newUrl);
		}
	}, []);

	useEffect(() => {
		window.scrollTo(0, 0);
		if (
			navigatePath &&
			navigatePath !== navStack.current?.[navStack.current?.length - 1]
		) {
			navStack.current.push(navigatePath);
		}
	}, [navigatePath]);

	useEffect(() => {
		if (fromProspect && props?.selectedPack) {
			const parseJSONPAck = JSON.parse(props?.selectedPack);
			if (!!parseJSONPAck && typeof parseJSONPAck == "object") {
				dispatch(getSessionCart());
				dispatch(setIsEditPackageFlow(true));
				dispatch(setEditPackagePackageId(parseJSONPAck?.packageId));
				dispatch(setSelectedGWProduct(parseJSONPAck?.sorDeviceType));
				dispatch(setSelectedBussProduct(parseJSONPAck));
				// dispatch(setLoadMobilityProductDetails(true));
				navigate("marketplace/pdp");
			}
		}else if(fromProspect){
			navigate("marketplace/gridwall");
		}
	}, [props?.selectedPack]);

	const ganvCartIcon = document.getElementById("top-header-cart-icn");
	if (ganvCartIcon) {
		ganvCartIcon.addEventListener("click", () => {
			const cartDot = document.getElementById("red-dot-buss");
			//global shoppingcart
			if (cartDot) {
				// dispatch(setLoadShoppingCartWidget(true));
				// dispatch(setLoadMobilityProductDetails(false));
				// dispatch(setLoadCheckoutWidget(false));
				navigate("/shop/shopping-cart");
			}
		});
	}
	if(ganvCartIcon){
	const ganvItemClient = ganvCartIcon.getBoundingClientRect()
		const mockCartIcon = document.getElementById("mck-crt-icon-id");
		if (mockCartIcon) {
			mockCartIcon.style.left = `${ganvItemClient.left}px`;
			mockCartIcon.style.top = `${ganvItemClient.top}px`;
		}
	}
	useEffect(()=>{
		if(!globalData?.storePreviousPaths && entryPoint) dispatch(setStorePreviousPaths([defaultPath]))
	},[entryPoint,globalData])
	useEffect(() => {
		//Gnav icon change
		if (Object.keys(sessionCartData || {})?.length) {
			const gnav = document.getElementById("top-header-cart-icn");
			const cartDotMain = document.getElementById("red-dot-buss");
			if (gnav && !cartDotMain) {
				const cartDot = document.createElement("div");
				cartDot.id = "red-dot-buss";
				gnav.appendChild(cartDot);
			}
		}else if(!sessionCartData || !Object.keys(sessionCartData).length){
		const cartDotMain = document.getElementById("red-dot-buss")
		if(cartDotMain) cartDotMain.removeAttribute("id")
	}
	}, [sessionCartData]);

	window.addEventListener("loadShoppingCartFromMiniCartForLoggedIn",()=>{
		const currentUrl = window.location.href;
		const newUrl = new URL(currentUrl);
		const searchParams = new URLSearchParams(newUrl?.search);
		if (searchParams.has("pageName")) {
			newUrl.searchParams.set("pageName", encodeURIComponent("/shop/shopping-cart"));
			window && window.history.pushState({}, "", newUrl);
		}
		dispatch(setNavigatePath("/shop/shopping-cart"));
	})

	const defaultPath = useMemo(() => {
		switch (entryPoint) {
			case "WCMP":
				return "wcmp";
			case "fwa":
				return "fwa/qualification-landing";
			case "movers":
				return "movers/movers-landing";
			case "cpc":
					return "fwa/plans";	
			case "softwares":
				return "marketplace/gridwall";
			case "marketplace":
				return "buss/addons";
			case "newline":
				return "/shop/select-account";
			case "retrieveCart":
					return "/shop/shopping-cart";
			case "accessory":
				return "/shop/accessories";	
		    case "byod":
				return "/shop/select-account";					
			default:
				return "gridwall/all";
		}
	}, [entryPoint]);
	useEffect(()=>
	{
		dispatch(setEntryPoint(entryPoint));
     
    },[navigatePath,entryPoint,dispatch]);
	useEffect(()=>
		{
			if(!entryPointMatched)
			{
				dispatch(setNavigatePath(defaultPath));
			}
		 
		},[entryPointMatched,defaultPath,dispatch]);

	const routeComponent = useMemo(() => {
		// const mappedComponent = ComponentPathMap?.[navigatePath];
		// //dispatch(setEntryPoint(entryPoint))
		// const entryPointMatched = entryPoint === globalData?.entryPoint
		// if (mappedComponent && entryPointMatched) {
		// 	return mappedComponent;
		// }
		// else{
		// !entryPointMatched && dispatch(setNavigatePath(defaultPath));
		// return ComponentPathMap?.[defaultPath];
		// }

		return (mappedComponent && entryPointMatched) ?
		mappedComponent : ComponentPathMap?.[defaultPath]
	}, [mappedComponent, entryPointMatched,defaultPath]);

	return routeComponent;
};
